import React, { useState, useEffect } from 'react';
import Timeline from './Timeline';
import DateRangesReport from './DateRangesReport';
import './ActivityAnalysis.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const ActivityAnalysis = () => {
    const [data, setData] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [hoveredItem, setHoveredItem] = useState(null);

    useEffect(() => {
        fetchActivityData();
    }, [dateRange, selectedCommunities]);

    const fetchActivityData = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                start_date: dateRange.start,
                end_date: dateRange.end
            });
            if (selectedCommunities.length > 0) {
                selectedCommunities.forEach(id => params.append('community_id', id));
            }

            const response = await apiFetch(`${API_BASE_URL}/monitoring/activity-coverage?${params.toString()}`);
            if (!response.ok) throw new Error('Failed to fetch activity data');

            const result = await response.json();
            setData(result.data || []);
            setCommunities(result.communities || []);
            setAllCommunities(result.all_communities || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDateRange = () => {
        const dates = [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            dates.push(new Date(d).toISOString().split('T')[0]);
        }
        return dates;
    };

    const getDayStatus = (communityId, date) => {
        const dayData = data.filter(i => i.public_id === communityId && i.date === date);
        if (dayData.length === 0) return 'empty';
        if (dayData.length === 1) {
            const count = dayData[0].session_count || 1;
            return count === 1 ? 'single' : 'duplicate';
        }
        return 'duplicate';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'empty': return 'status-empty';
            case 'single': return 'status-single';
            case 'duplicate': return 'status-duplicate';
            default: return 'status-empty';
        }
    };

    const getStatusTooltip = (communityId, date) => {
        const dayData = data.filter(item => item.public_id === communityId && item.date === date);
        if (dayData.length === 0) return `Нет данных для ${date}`;
        const totalRecords = dayData.reduce((s, i) => s + (i.record_count || 0), 0);
        const totalSessions = dayData.reduce((s, i) => s + (i.session_count || 0), 0);
        return `
            Дата: ${date}
            Записей: ${totalRecords}
            Уникальных сессий: ${totalSessions}
            ${totalSessions > 1 ? '⚠️ Обнаружены дубли' : ''}
        `;
    };

    const dateRangeOptions = [
        { label: '7 дней', days: 7 },
        { label: '30 дней', days: 30 },
        { label: '90 дней', days: 90 },
        { label: '180 дней', days: 180 },
        { label: '365 дней', days: 365 }
    ];

    const handleDateRangeChange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    const dates = generateDateRange();

    return (
        <div className="activity-analysis">
            <div className="activity-header">
                <h2><i className="fas fa-chart-line"></i> Анализ заполненности активности</h2>
                <p>Визуализация наличия данных активности по группам и датам</p>
            </div>

            <div className="activity-filters">
                <div className="filter-block">
                    <label>Временной диапазон</label>
                    <div className="filter-buttons">
                        {dateRangeOptions.map(opt => (
                            <button
                                key={opt.days}
                                onClick={() => handleDateRangeChange(opt.days)}
                                className={`filter-btn ${
                                    (new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24) === opt.days
                                        ? 'active' : ''
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="filter-block">
                    <label>Пользовательский диапазон</label>
                    <div className="date-inputs">
                        <input type="date" value={dateRange.start}
                               onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}/>
                        <span>—</span>
                        <input type="date" value={dateRange.end}
                               onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}/>
                    </div>
                </div>

                <div className="filter-block">
                    <label>Фильтр по группам ({allCommunities.length} групп)</label>
                    <div className="community-list">
                        {allCommunities.map(c => (
                            <label key={c.id}>
                                <input
                                    type="checkbox"
                                    checked={selectedCommunities.includes(c.id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCommunities(prev => [...prev, c.id]);
                                        } else {
                                            setSelectedCommunities(prev => prev.filter(id => id !== c.id));
                                        }
                                    }}
                                />
                                <span title={c.name}>{c.name}</span>
                            </label>
                        ))}
                    </div>
                    {selectedCommunities.length > 0 && (
                        <button className="clear-filter" onClick={() => setSelectedCommunities([])}>
                            Очистить фильтр
                        </button>
                    )}
                </div>

                <div className="refresh-block">
                    <button onClick={fetchActivityData}>
                        <i className="fas fa-sync-alt"></i> Обновить
                    </button>
                </div>
            </div>

            <div className="legend">
                <div><div className="status-empty"></div><span>Нет данных</span></div>
                <div><div className="status-single"></div><span>Одно значение</span></div>
                <div><div className="status-duplicate"></div><span>Дубли</span></div>
            </div>

            {loading && (
                <div className="loading-state">
                    <i className="fas fa-circle-notch fa-spin"></i>
                    <p>Загрузка данных...</p>
                </div>
            )}

            {error && (
                <div className="error-state">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{error}</p>
                    <button onClick={fetchActivityData}>Повторить</button>
                </div>
            )}

            {!loading && !error && (
                <div className="activity-content">
                    <Timeline
                        communities={communities}
                        dates={dates}
                        data={data}
                        getDayStatus={getDayStatus}
                        getStatusColor={getStatusColor}
                        getStatusTooltip={getStatusTooltip}
                        hoveredItem={hoveredItem}
                        setHoveredItem={setHoveredItem}
                    />

                    <div className="summary">
                        <div><h3>Всего групп</h3><p>{communities.length}</p></div>
                        <div><h3>Дней в диапазоне</h3><p>{dates.length}</p></div>
                        <div><h3>Записей с данными</h3><p>{data.length}</p></div>
                        <div><h3>Дней с дублями</h3>
                            <p className="duplicates-count">
                                {(() => {
                                    const dup = new Set();
                                    communities.forEach(c => {
                                        dates.forEach(d => {
                                            if (getDayStatus(c.id, d) === 'duplicate') dup.add(d);
                                        });
                                    });
                                    return dup.size;
                                })()}
                            </p>
                        </div>
                    </div>

                    <div className="date-report">
                        <h3>Отчет по диапазонам дат</h3>
                        <DateRangesReport 
                            communities={communities} 
                            dates={dates} 
                            getDayStatus={getDayStatus}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityAnalysis;
